import { Refresh } from '@mui/icons-material'
import { Chip, IconButton, Link, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tooltip } from '@mui/material'
import { useContext, useEffect, useMemo, useState } from 'react'
import { ColumnType } from '../../component/ReadTable/ReadTable'
import { getBulkReports } from '../../service/Service'
import { AppContext } from '../../store/AppProvider'
import { SHOW_ERROR_ALERT } from '../../store/reducer/AlertNotifierReducer'
import { getDateWithTime, getShortenedFileName } from '../../utils/Utils'
import './BulkReport.css'
import { ErrorConstant } from '../../utils/Constants'

export const ViewBulkReport = () => {
    const { setShowLoader, setAlert } = useContext(AppContext)
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [list, setList] = useState([])
    const [refresh, setRefresh] = useState(false)

    useEffect(() => {
        const fetchList = async () => {
            try {
                setShowLoader(true)
                const listData = await getBulkReports()
                setList(listData)
            } catch (error) {
                setAlert({ type: SHOW_ERROR_ALERT, alertMessage: ErrorConstant.error })
            } finally {
                setShowLoader(false)
            }
        }
        fetchList()
    }, [setAlert, setShowLoader, refresh])

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const headers = [
        { key: 'report_id', value: 'Report Id' },
        { key: 'requested_time', value: 'Requested Time' },
        { key: 'status', value: 'Status', type: ColumnType.chip },
        { key: 'download_url', value: 'Download URL' }
    ]

    const getStatus = (rowValue) => {
        const completeStatus = rowValue?.toLowerCase() === 'completed'
        const getChipColor = () => {
            if (completeStatus) return 'success'
            else if (rowValue?.toLowerCase() === 'initiated') return 'primary'
            else if (rowValue?.toLowerCase() === 'pending') return 'warning'
            else if (rowValue?.toLowerCase() === 'failed') return 'error'
            else return 'error'
        }
        return <Chip label={rowValue} color={getChipColor()} variant={completeStatus ? 'filled' : 'outlined'} />
    }

    const downloadPdf = (pdfUrl) => {
        window.open(pdfUrl, '_blank');
    }

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - list?.length) : 0;

    const visibleRows = useMemo(
        () =>
            [...list]
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [list, page, rowsPerPage],
    );

    return (
        <div>
            <div className='header-title' style={{ letterSpacing: '0.5px' }}>VIEW BULK REPORTS</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 18 }}>Generated Report Details</div>
                <IconButton
                    color="primary"
                    onClick={() => setRefresh(!refresh)}>
                    <Tooltip title="Refresh">
                        <Refresh />
                    </Tooltip>
                </IconButton>
            </div>
            <Paper sx={{ width: '100%', mb: 2 }}>
                <TableContainer id='bulk_report_id'>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                {headers?.map((ele, index) => <TableCell key={index + ele?.key} align={"left"} sx={{ fontWeight: 'bold' }}>
                                    <>{ele?.value}</>
                                </TableCell>)}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visibleRows?.map((row, index) => (
                                <TableRow key={index}>
                                    <TableCell
                                        align={"left"}>
                                        {row?.['report_id']}
                                    </TableCell>
                                    <TableCell
                                        align={"left"}>
                                        {getDateWithTime(row?.['requested_time'])}
                                    </TableCell>
                                    <TableCell
                                        align={"left"}>
                                        {getStatus(row?.['status'])}
                                    </TableCell>
                                    <TableCell
                                        align={"left"}>
                                        {<Link component="button"
                                            variant="body2"
                                            onClick={() => downloadPdf(row?.['download_url'])}>{getShortenedFileName(row?.['download_url'])}</Link>}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {emptyRows > 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} />
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    {!list?.length
                        ? <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 10 }}>
                            No Records to display
                        </div>
                        : <div />
                    }
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[10, 25]}
                    component="div"
                    count={list?.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>
        </div>
    )
}
